import { IVector4 } from '../../api.js';
import { mark_mesh_dirty_for_lightsystem } from '../MeshManager.js';
import { SceneAssetMeshInstanceMetadata } from './SceneAssetMetadata.js';
import { ALBEDO_ROUGHNESS_INSTANCE_ATTRIBUTE_NAME, ViewerMaterial } from '../material/ViewerMaterial.js';
import { Vector4 as BABYLON_Vector4 } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.js';
import { SceneAssetMeshVariantKey } from './SceneAssetMeshVariantKey.js';
import { Observable } from '../../Observable.js';
import { RenderingGroupID } from '../Viewer.js';
let custom_vertex_data_id_counter = 1;
const _TMP_ONE4 = BABYLON_Vector4.One();
export class SceneAssetMeshInstance {
    constructor(asset_instance, source) {
        this._mesh_variant_key_is_dirty = false;
        this._mesh_variant_key_world_transform_determinant_dirty = false;
        this._color_roughness_factor = new BABYLON_Vector4(1, 1, 1, 1);
        this.rendering_material_changed_observable = new Observable();
        this._has_last_rendering_material = false;
        this._last_rendering_material = null;
        this._custom_material = null;
        this._asset_instance = asset_instance;
        this.source = source;
        this._mesh_variant_key = new SceneAssetMeshVariantKey();
        this.babylon_instance = this.source.instantiate_mesh_instance(this);
        this.babylon_instance.instancedBuffers[ALBEDO_ROUGHNESS_INSTANCE_ATTRIBUTE_NAME] = this._color_roughness_factor;
        this.babylon_instance.renderingGroupId = RenderingGroupID.DEFAULT_RENDERING_GROUP;
        this._on_world_transform_changed_callback = this.on_world_transform_changed.bind(this);
        this.babylon_instance.registerAfterWorldMatrixUpdate(this._on_world_transform_changed_callback);
        this.metadata = new SceneAssetMeshInstanceMetadata(this._asset_instance.metadata, source);
        this.source.viewer.viewer_scene.assign_metadata_to_babylon_mesh(this.babylon_instance, this.metadata);
        this._on_world_transform_changed_callback();
        this.update_material_color_roughness_factor_feature_enabled();
        this.mark_variant_key_dirty();
    }
    dispose() {
        this.rendering_material_changed_observable.dispose();
        this.source.viewer.outline_layer.remove_mesh(this.babylon_instance);
        mark_mesh_dirty_for_lightsystem(this.source.viewer.light_system, this.babylon_instance);
        this.source.viewer.freezing_manager.set_all_materials_dirty();
        this.source.viewer.mark_scene_dirty();
        this.source.viewer.mark_world_bounds_dirty();
        this.source.unregister_instance(this);
        const existing_source = this.babylon_instance.sourceMesh;
        this.babylon_instance.dispose(true, false);
        this.source.dispose_unused_variant_source(this, existing_source);
    }
    copy_settings_from(other) {
        this._custom_material = other._custom_material;
        this._mesh_variant_key.receive_shadows = other._mesh_variant_key.receive_shadows;
        this._mesh_variant_key.transform_positive_determinant = other._mesh_variant_key.transform_positive_determinant;
        this._mesh_variant_key.transparent_model_id = other._mesh_variant_key.transparent_model_id === null ? null : this.babylon_instance.uniqueId;
        this._mesh_variant_key.custom_source_vertex_data_id = other._mesh_variant_key.custom_source_vertex_data_id;
        this._mesh_variant_key_world_transform_determinant_dirty = true;
        this.mark_variant_key_dirty();
    }
    set_clip_plane(plane) {
        this._mesh_variant_key.clip_plane = plane;
        this.mark_variant_key_dirty();
    }
    on_world_transform_changed() {
        this._mesh_variant_key_world_transform_determinant_dirty = true;
        this.mark_variant_key_dirty();
        mark_mesh_dirty_for_lightsystem(this.source.viewer.light_system, this.babylon_instance);
    }
    set_custom_material(material) {
        if (this._custom_material === material)
            return;
        this._custom_material = material;
        this.mark_variant_key_dirty();
    }
    get_custom_material() {
        return this._custom_material;
    }
    get_source_material() {
        return this.source.source_material;
    }
    get_rendering_material() {
        if (this._asset_instance.get_override_material() !== null)
            return this._asset_instance.get_override_material();
        if (this.get_custom_material() !== null)
            return this.get_custom_material();
        return this.source.source_material;
    }
    set_receive_shadows(value) {
        if (this._mesh_variant_key.receive_shadows === value)
            return;
        this._mesh_variant_key.receive_shadows = value;
        this.mark_variant_key_dirty();
    }
    prepare_for_render() {
        var _a;
        if (this._mesh_variant_key_is_dirty) {
            this._mesh_variant_key_is_dirty = false;
            const rendering_material = this.get_rendering_material();
            if (rendering_material !== null && (rendering_material.transparencyMode === 2 || rendering_material.transparencyMode === 3 || rendering_material.needAlphaBlending())) {
                this._mesh_variant_key.transparent_model_id = this.babylon_instance.uniqueId;
            }
            else {
                this._mesh_variant_key.transparent_model_id = null;
            }
            if (this._mesh_variant_key_world_transform_determinant_dirty) {
                this._mesh_variant_key_world_transform_determinant_dirty = false;
                this._mesh_variant_key.transform_positive_determinant = this.babylon_instance._getWorldMatrixDeterminant() >= 0.0;
            }
            if (this._has_last_rendering_material && this._last_rendering_material !== rendering_material) {
                this.rendering_material_changed_observable.notify(this, rendering_material);
            }
            this._last_rendering_material = rendering_material;
            this._has_last_rendering_material = true;
            this._mesh_variant_key.custom_material = (_a = this._asset_instance.get_override_material()) !== null && _a !== void 0 ? _a : this.get_custom_material();
            this.source.update_source_mesh_key_for_instance(this);
            this.update_material_color_roughness_factor_feature_enabled();
        }
    }
    get mesh_variant_key() {
        return this._mesh_variant_key;
    }
    mark_variant_key_dirty() {
        this._mesh_variant_key_is_dirty = true;
        this.source.viewer.mark_scene_dirty();
    }
    set_color_roughness_factor(factor) {
        if (IVector4.equals_with_epsilon(factor, this._color_roughness_factor, 0.001)) {
            return;
        }
        this._color_roughness_factor.copyFromFloats(factor.x, factor.y, factor.z, factor.w);
        this.source.viewer.mark_scene_dirty();
        this.update_material_color_roughness_factor_feature_enabled();
    }
    update_material_color_roughness_factor_feature_enabled() {
        const material = this.get_rendering_material();
        if (material !== null && material instanceof ViewerMaterial) {
            if (!material.enable_albedo_roughness_factor && !IVector4.equals_with_epsilon(this._color_roughness_factor, _TMP_ONE4, 0.01)) {
                material.enable_albedo_roughness_factor = true;
            }
        }
    }
    load_unique_vertex_source_data(require_new_data) {
        if (require_new_data || this._mesh_variant_key.custom_source_vertex_data_id === null) {
            this._mesh_variant_key.custom_source_vertex_data_id = custom_vertex_data_id_counter++;
            // Mark our variant key dirty and call prepare_for_render to immediately get our new vertex data, that the user will expect after calling this function
            this.mark_variant_key_dirty();
            this.prepare_for_render();
        }
        return this.babylon_instance.sourceMesh;
    }
    unload_unique_vertex_source_data() {
        if (this._mesh_variant_key.custom_source_vertex_data_id !== null) {
            this._mesh_variant_key.custom_source_vertex_data_id = null;
            // Mark our variant key dirty and call prepare_for_render to immediately get our new (original) vertex data, that the user will expect after calling this function
            this.mark_variant_key_dirty();
            this.prepare_for_render();
        }
    }
}
//# sourceMappingURL=SceneAssetMeshInstance.js.map